import api from './axiosInstance';
import { Role, CreateRoleData, UpdateRoleData, PermissionConfig } from '../types/role';

export interface RoleQueryParams {
  page?: number;
  limit?: number;
  sortBy?: string;
  name?: string;
  status?: string | null;
  includeInactive?: boolean;
  includeUsers?: boolean;
  department?: string;
}

export interface PaginatedRoleResponse {
  success: boolean;
  message: string;
  data: Role[];
  meta: {
    page: number;
    limit: number;
    totalPages: number;
    totalDocs: number;
    hasPrevPage: boolean;
    hasNextPage: boolean;
    prevPage: number | null;
    nextPage: number | null;
  };
}

export class RoleService {
  /**
   * Get permissions configuration
   */
  static async getPermissionsConfig(department?: string): Promise<PermissionConfig> {
    const params = department ? { department } : {};
    const response = await api.get('/roles/permissions-config', { params });
    return response.data.data;
  }

  /**
   * Get all roles with optional filtering and pagination
   */
  static async getRoles(params?: RoleQueryParams): Promise<PaginatedRoleResponse> {
    const response = await api.get('/roles', { params });
    return response.data;
  }

  /**
   * Get all roles for dropdown/DDL purposes (no pagination)
   */
  static async getAllRolesForDDL(): Promise<Role[]> {
    const response = await api.get('/roles/all');
    return response.data.data;
  }

  /**
   * Get all approver roles (for approval workflow dropdown)
   */
  static async getApproverRoles(department?: string): Promise<Role[]> {
    const params = department ? { department } : {};
    const response = await api.get('/roles/approvers', { params });
    return response.data.data;
  }

  /**
   * Get roles with user count
   */
  static async getRolesWithUserCount(): Promise<Role[]> {
    const response = await api.get('/roles/with-user-count');
    return response.data;
  }

  /**
   * Get a single role by ID
   */
  static async getRoleById(id: string): Promise<Role> {
    const response = await api.get(`/roles/${id}`);
    return response.data;
  }

  /**
   * Create a new role
   */
  static async createRole(data: CreateRoleData): Promise<Role> {
    const response = await api.post('/roles', data);
    // API returns { success, message, data } - extract the data (role object)
    return response.data.data || response.data;
  }

  /**
   * Update an existing role
   */
  static async updateRole(id: string, data: UpdateRoleData): Promise<Role> {
    const response = await api.patch(`/roles/${id}`, data);
    // API returns { success, message, data } - extract the data (role object)
    return response.data.data || response.data;
  }

  /**
   * Delete a role
   */
  static async deleteRole(id: string): Promise<{ message: string }> {
    const response = await api.delete(`/roles/${id}`);
    return response.data;
  }

  /**
   * Activate a role
   */
  static async activateRole(id: string): Promise<Role> {
    const response = await api.patch(`/roles/${id}/activate`);
    return response.data;
  }

  /**
   * Deactivate a role
   */
  static async deactivateRole(id: string): Promise<Role> {
    const response = await api.patch(`/roles/${id}/deactivate`);
    return response.data;
  }

  /**
   * Refresh role rights in the system
   */
  static async refreshRoleRights(): Promise<{ message: string }> {
    const response = await api.post('/roles/refresh-rights');
    return response.data;
  }

  /**
   * Get current role rights configuration
   */
  static async getCurrentRoleRights(): Promise<any> {
    const response = await api.get('/roles/current-rights');
    return response.data;
  }

  /**
   * Get menu order configuration for a role
   */
  static async getRoleMenuOrder(roleId: string): Promise<{
    success: boolean;
    message: string;
    data: {
      roleId: string;
      roleName: string;
      menuItems: any[];
      menuOrder: {
        menuItems: Array<{
          menuKey: string;
          order: number;
          enabled?: boolean;
          hidden?: boolean;
          icon?: string;
          subItems?: Array<{
            subMenuKey: string;
            order: number;
            enabled?: boolean;
            hidden?: boolean;
            icon?: string;
          }>;
        }>;
      };
    };
  }> {
    const response = await api.get(`/roles/${roleId}/menu-order`);
    return response.data;
  }

  /**
   * Update menu order configuration for a role
   */
  static async updateRoleMenuOrder(roleId: string, menuOrder: {
    menuItems: Array<{
      menuKey: string;
      order: number;
      enabled?: boolean;
      hidden?: boolean;
      icon?: string;
      subItems?: Array<{
        subMenuKey: string;
        order: number;
        enabled?: boolean;
        hidden?: boolean;
        icon?: string;
      }>;
    }>;
  }): Promise<{
    success: boolean;
    message: string;
    data: Role;
  }> {
    const response = await api.put(`/roles/${roleId}/menu-order`, { menuOrder });
    return response.data;
  }
}

// Export instance for consistency with other services
export const roleService = RoleService;

export default RoleService; 